import { Table, Tag, Space, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { UseMutateAsyncFunction, useMutation } from "react-query";
import { deleteModifier, getModifierById } from "../../../api/modifier";
import { AddEditModifier } from "./AddEditModifier";
import { ModifierType } from "./ModifierType";
import EditButton from "../../UI/EditButton/editbutton";
import DeleteButton from "../../UI/DeleteButton/deletebutton";
import DeleteModal from "../../UI/ModalComponent/DeleteModal/deletemodal";
import { AxiosResponse } from "axios";

interface inventoryProps {
  paginatedModifier: any;
  isModifierLoading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  filter: boolean | undefined;
  search: string;
  setPaginatedModifier: Dispatch<any>;
  ModifierMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  tableKey: string;
}
interface ModifiersInterface {
  id: number;
  name: string;
  isActive: boolean;
}

const ModifierList = ({
  paginatedModifier,
  isModifierLoading,
  page,
  setPage,
  filter,
  search,
  setPaginatedModifier,
  ModifierMutation,
  tableKey,
}: inventoryProps) => {
  const [t] = useTranslation("common");
  const [Modifier, setModifier] = useState<ModifierType>();
  const [openModifier, setOpenModifier] = useState(false);
  const [selectedModifier, setSelectedModifier] = useState<any>();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const [, setError] = useState("");
  const getModifierMutation = useMutation("getModifier", getModifierById);
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      ModifierMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedModifier(res.data?.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  function fillModifier(id: any) {
    getModifierMutation.mutate(id, {
      onSuccess: (res) => {
        setModifier(res.data);
        setOpenModifier(true);
      },
    });
  }

  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };

  const deleteModifierMutation = useMutation("deleteModifier", deleteModifier, {
    onSuccess: () => {
      ModifierMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res) => {
        setPaginatedModifier(res.data?.data);
        message.success("Complete successfully");
      });
    },
    onError,
  });

  const DeleteModifier = (id: string | undefined, name: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteModifierMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const columns: ColumnsType<ModifiersInterface> = [
    {
      title: `${t("modifier")}`,
      dataIndex: "name",
      key: "name",

      className: "table-header",
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      align: "center",
      width: "5rem",
      key: "isActive",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      key: "action",
      fixed: "right",
      width: "5rem",
      align: "center",
      render: (record: any) => (
        <Space>
          <EditButton
            onClick={() => {
              fillModifier(record.id);
              setSelectedModifier(record.id);
            }}
          />
          <DeleteButton
            onClick={() => DeleteModifier(record?.id, record?.name)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />

      {openModifier && Modifier && (
        <AddEditModifier
          Modifier={Modifier}
          openModifier={openModifier}
          setOpenModifier={setOpenModifier}
          id={selectedModifier}
          setPaginatedModifier={setPaginatedModifier}
          ModifierMutation={ModifierMutation}
          page={page}
          filter={filter}
          search={search}
        />
      )}

      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={paginatedModifier}
        loading={isModifierLoading}
        pagination={{ pageSize: 8 }}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record) => record.id}
      />
    </>
  );
};

export default ModifierList;
